import { useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { useStartAssessmentMutation } from "../../api/muzakereApi";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";

/** Sunucunun uyarısı (boşsa null). */
export const warningOf = (result) => {
  const w = result?.data?.data?.warning;
  return typeof w === "string" && w.trim() ? w.trim() : null;
};

/**
 * Değerlendirmeyi başlat (mobil GroupMuzakerePanel.handleStart): önce deneme
 * (dryRun) sunucu kurallarını denetler; aynı sorular bu gruba son 60 günde
 * sorulduysa uyarı onayda gösterilir. Onaylanınca gerçekten başlatılır,
 * gün sonuna kadar açık kalır ve üyelere bildirim gider.
 * muzakere verildiğinde açılır; kapanınca onClose.
 */
export default function StartAssessmentDialog({ muzakere, groupId, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const paperSx = useDialogPaperSx();
  const [start, { isLoading: starting }] = useStartAssessmentMutation();
  const [phase, setPhase] = useState("checking"); // checking | confirm
  const [warning, setWarning] = useState(null);
  const inFlight = useRef(false);
  const id = muzakere?.id;

  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setPhase("checking");
    setWarning(null);
    start({ muzakereId: id, groupId, dryRun: true }).then((result) => {
      if (!alive) return;
      if (result.error) {
        toast.error(result.error.message || t("mza_start_failed"));
        onClose();
        return;
      }
      setWarning(warningOf(result));
      setPhase("confirm");
    });
    return () => {
      alive = false;
    };
    // Yalnız açılışta (müzakere değişince) denenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const confirm = async () => {
    if (inFlight.current || !id) return;
    inFlight.current = true;
    const result = await start({ muzakereId: id, groupId });
    inFlight.current = false;
    if (result.error) toast.error(result.error.message || t("mza_start_failed"));
    else toast.success(t("mza_started"));
    onClose();
  };

  return (
    <Dialog
      open={!!muzakere}
      onClose={() => !starting && onClose()}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 3, ...paperSx } }}
    >
      <DialogTitle sx={{ fontWeight: 500 }}>{t("mza_start_title")}</DialogTitle>
      <DialogContent>
        {phase === "checking" ? (
          <div className="flex justify-center py-6">
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {warning ? (
              <p role="alert" className="flex items-start gap-2 text-sm text-[#B7791F] dark:text-[#F3C77A]">
                <WarningAmberIcon fontSize="small" />
                <span>{warning}</span>
              </p>
            ) : null}
            <p className={`text-sm ${cls.title}`}>{t("mza_start_body")}</p>
            {muzakere?.title ? <p className={`text-xs ${cls.muted}`}>{muzakere.title}</p> : null}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SoftButton tone="cancel" onClick={onClose} disabled={starting && phase === "confirm"}>
          {t("muzakere_cancel")}
        </SoftButton>
        <SoftButton
          tone="success"
          startIcon={starting ? null : <PlayCircleOutlineIcon />}
          onClick={confirm}
          disabled={phase !== "confirm" || starting}
        >
          {starting && phase === "confirm" ? <CircularProgress size={18} color="inherit" /> : t("mza_start")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
